// Las anotaciones del lienzo —texto, nota, comentario— entre lo guardado y React Flow —
// ADR-2026-09-16-el-lienzo-toma-la-barra-de-flujos.
//
// Los nodos son los de Flujos (`NodoTexto`, `NodoNota`, `NodoComentario`), que leen
// `data.params`; acá se arma esa forma con los mismos defaults y desplazamientos con los
// que Flujos los crea al tocar el fondo (`crearNodo` + `onPaneClick` de `FlujoEditor`),
// y se vuelve a lo guardable sin nada de la sesión (`selected`, `measured`, `dragging`).
// Puro: sin React ni red.

import type { Node } from '@xyflow/react';
import type { Anotacion, TipoDeAnotacion } from './persistenciaDelLienzo';

export interface UsuarioDelLienzo {
  id: string;
  nombre: string;
}

/** Los ids de las anotaciones llevan prefijo: una celda es un id de imagen (uuid). */
const PREFIJO = 'anot-';

export const esAnotacion = (n: { id: string; type?: string }): boolean =>
  n.id.startsWith(PREFIJO) && (n.type === 'texto' || n.type === 'nota' || n.type === 'comentario');

/** Params iniciales y tamaño, los mismos que el registry de Flujos. */
function defaults(tipo: TipoDeAnotacion, usuario: UsuarioDelLienzo): Pick<Anotacion, 'params' | 'style'> {
  if (tipo === 'texto') {
    return { params: { texto: '', tamano: 'grande', autorId: usuario.id, autorNombre: usuario.nombre }, style: { width: 320 } };
  }
  if (tipo === 'nota') return { params: { texto: '', color: 'amarillo' }, style: { width: 220, height: 140 } };
  return { params: { hilo: [], resuelto: false } };
}

/** Dónde cae respecto del click, como en Flujos: el texto arranca en el punto, la nota
 *  se centra, el pin queda bajo el puntero. */
function corrimiento(tipo: TipoDeAnotacion): { x: number; y: number } {
  if (tipo === 'texto') return { x: 0, y: -20 };
  if (tipo === 'nota') return { x: -110, y: -70 };
  return { x: -16, y: -16 };
}

export function nuevaAnotacion(
  tipo: TipoDeAnotacion,
  punto: { x: number; y: number },
  usuario: UsuarioDelLienzo,
  id = `${PREFIJO}${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`,
): Anotacion {
  const d = corrimiento(tipo);
  return { id, tipo, position: { x: punto.x + d.x, y: punto.y + d.y }, ...defaults(tipo, usuario) };
}

/** El nodo de React Flow. El pin abierto sube a 2000 (lo pone el lienzo); el resto, 500. */
export function nodoDeAnotacion(a: Anotacion, selected = false): Node {
  return {
    id: a.id,
    type: a.tipo,
    position: a.position,
    ...(a.style ? { style: a.style } : {}),
    data: { tipo: a.tipo, params: a.params, status: 'idle', history: [] },
    ...(a.tipo === 'comentario' ? { zIndex: 500 } : {}),
    selected,
  };
}

/** De vuelta a lo guardable, o `null` si el nodo no es una anotación. */
export function anotacionDeNodo(n: Node): Anotacion | null {
  if (!esAnotacion(n)) return null;
  const params = (n.data as { params?: unknown }).params;
  const style: Anotacion['style'] = {};
  if (typeof n.style?.width === 'number') style.width = n.style.width;
  if (typeof n.style?.height === 'number') style.height = n.style.height;
  return {
    id: n.id,
    tipo: n.type as TipoDeAnotacion,
    position: { x: n.position.x, y: n.position.y },
    ...(Object.keys(style).length > 0 ? { style } : {}),
    params: params && typeof params === 'object' ? (params as Record<string, unknown>) : {},
  };
}

/** Las anotaciones de una lista de nodos, en orden. */
export function anotacionesDeNodos(nodes: readonly Node[]): Anotacion[] {
  const out: Anotacion[] = [];
  for (const n of nodes) {
    const a = anotacionDeNodo(n);
    if (a) out.push(a);
  }
  return out;
}
